import Template from "./template";
import paper from "paper";
import ComponentPort from "../core/componentPort";
import { LogicalLayerType } from "../core/init";
import { CompoundPath } from "paper/dist/paper-core";
import { DEFAULT_CHANNEL_WIDTH_UM } from "./channelWidths";
import { resolveBinaryLeafCount } from "./branchLeafs";

export default class YTree extends Template {
    constructor() {
        super();
    }

    __setupDefinitions(): void  {
        this.__unique = {
            position: "Point"
        };

        this.__heritable = {
            componentSpacing: "Float",
            flowChannelWidth: "Float",
            rotation: "Float",
            leafSpace: "Float",
            in: "Integer",
            out: "Integer",
            depth: "Float",
            stageSpace: "Float",
            mirrorByX: "Float",
            mirrorByY: "Float"
        };

        this.__defaults = {
            componentSpacing: 2000,
            flowChannelWidth: DEFAULT_CHANNEL_WIDTH_UM,
            rotation: 0,
            leafSpace: 4 * 1000,
            in: 1,
            out: 8,
            depth: 250,
            stageSpace: 4000,
            mirrorByX: 0,
            mirrorByY: 0
        };

        this.__units = {
            componentSpacing: "μm",
            flowChannelWidth: "μm",
            rotation: "°",
            leafSpace: "μm",
            in: "",
            out: "",
            depth: "μm",
            stageSpace: "μm"
        };

        this.__minimum = {
            componentSpacing: 0,
            flowChannelWidth: 10,
            leafSpace: 30,
            in: 1,
            out: 2,
            depth: 10,
            stageSpace: 100,
            rotation: 0,
            mirrorByX: 0,
            mirrorByY: 0
        };

        this.__maximum = {
            componentSpacing: 10000,
            flowChannelWidth: 2000,
            leafSpace: 12000,
            in: 1,
            out: 128,
            depth: 1200,
            stageSpace: 6000,
            rotation: 360,
            mirrorByX: 1,
            mirrorByY: 1
        };

        this.__featureParams = {
            componentSpacing: "componentSpacing",
            position: "position",
            flowChannelWidth: "flowChannelWidth",
            rotation: "rotation",
            leafSpace: "leafSpace",
            in: "in",
            out: "out",
            stageSpace: "stageSpace",
            mirrorByX: "mirrorByX",
            mirrorByY: "mirrorByY"
        };

        this.__targetParams = {
            componentSpacing: "componentSpacing",
            flowChannelWidth: "flowChannelWidth",
            rotation: "rotation",
            leafSpace: "leafSpace",
            in: "in",
            out: "out",
            stageSpace: "stageSpace",
            mirrorByX: "mirrorByX",
            mirrorByY: "mirrorByY"
        };

        this.__placementTool = "componentPositionTool";

        this.__toolParams = {
            position: "position"
        };

        this.__renderKeys = ["FLOW"];

        this.__mint = "YTREE";

        this.__zOffsetKeys = {
            FLOW: "depth"
        };

        this.__substrateOffset = {
            FLOW: "0"
        };
    }

    __treeLeafSpace(params: { [k: string]: any }): number {
        const named = Number(params.leafSpace);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const legacy = Number(params.spacing);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 4000;
    }

    __treeStageSpace(params: { [k: string]: any }): number {
        const named = Number(params.stageSpace);
        if (Number.isFinite(named) && named > 0) {
            return named;
        }
        const legacy = Number(params.stageLength);
        if (Number.isFinite(legacy) && legacy > 0) {
            return legacy;
        }
        return 4000;
    }

    getPorts(params: { [k: string]: any }) {
        const ports = [];
        const spacing = this.__treeLeafSpace(params);
        // out must be ≥ 2 (same rule as MUX); pad non-powers of 2 upward.
        const leafs = Math.max(2, resolveBinaryLeafCount(params).leafs);
        const stagelength = this.__treeStageSpace(params);

        // Stadium-cap *centers* (not the outer rim). A RoundedChannel that
        // ends here fully overlaps the port circle instead of only kissing
        // the edge. Trunk pivot is the stem-cap center; leaves are the
        // last-level Y-twig pivots.
        ports.push(new ComponentPort(0, 0, "1", LogicalLayerType.FLOW));

        const levels = Math.log2(leafs);
        const w = spacing * (leafs / 2 + 1);

        const tips = this.__ytreeLeafTips(0, 0, stagelength, w, 1, levels);
        for (let i = 0; i < tips.length; i++) {
            ports.push(new ComponentPort(tips[i][0], tips[i][1], (2 + i).toString(), LogicalLayerType.FLOW));
        }

        return ports;
    }

    render2D(params: { [k: string]: any }, key: string) {
        const position = params.position;
        const cw = params.flowChannelWidth;
        let rotation = params.rotation;
        const spacing = this.__treeLeafSpace(params);
        const resolved = resolveBinaryLeafCount(params);
        const leafs = Math.max(2, resolved.leafs);
        const color = params.color;
        const stagelength = this.__treeStageSpace(params);
        const px = position[0];
        const py = position[1];

        const treepath = new paper.CompoundPath("");
        const levels = Math.log2(leafs);
        const w = spacing * (leafs / 2 + 1);
        this.__generateYTwig(treepath, px, py, cw, stagelength, w, 1, levels);

        treepath.fillColor = color;
        this.transformRender({ ...params, rotation }, treepath);

        return treepath;
    }

    render2DTarget(key: string | null, params: { [k: string]: any }) {
        if (key === null) {
            key = this.__renderKeys[0];
        }
        const render = this.render2D(params, key);
        render.fillColor!.alpha = 0.5;
        return render;
    }

    __ytreeLeafTips(px: number, py: number, stagelength: number, newspacing: number, level: number, maxlevel: number): number[][] {
        const hspacing = newspacing / 2;
        const lex = px - 0.5 * newspacing;
        const ley = py + stagelength;
        const rex = px + 0.5 * newspacing;
        const rey = py + stagelength;
        if (level === maxlevel) {
            const half = 0.5 * newspacing;
            // Right leaf first so port 2 matches the previous T-tree order.
            return [
                [px + half, py + stagelength],
                [px - half, py + stagelength]
            ];
        }
        return this.__ytreeLeafTips(rex, rey, stagelength, hspacing, level + 1, maxlevel).concat(
            this.__ytreeLeafTips(lex, ley, stagelength, hspacing, level + 1, maxlevel)
        );
    }

    __generateYTwig(treepath: paper.CompoundPath, px: number, py: number, cw: number, stagelength: number, newspacing: number, level: number, maxlevel: number, islast = false): void  {
        const hspacing = newspacing / 2;
        const lex = px - 0.5 * newspacing;
        const ley = py + stagelength;
        const rex = px + 0.5 * newspacing;
        const rey = py + stagelength;

        if (level === maxlevel) {
            islast = true;
        }

        this.__drawYtwig(treepath, px, py, cw, stagelength, newspacing, islast);

        if (!islast) {
            this.__generateYTwig(treepath, lex, ley, cw, stagelength, hspacing, level + 1, maxlevel);
            this.__generateYTwig(treepath, rex, rey, cw, stagelength, hspacing, level + 1, maxlevel);
        }
    }

    __drawYtwig(treepath: paper.CompoundPath, px: number, py: number, cw: number, stagelength: number, spacing: number, drawleafs = false): paper.CompoundPath  {
        const pivotpoint = new paper.Point(px, py);

        // stem
        const startPoint = new paper.Point(px - cw / 2, py - cw / 2);

        const angle = Math.atan(spacing / 2 / stagelength);

        const h = spacing / 2 / Math.sin(angle) + cw;

        // left leaf
        let rec = new paper.Path.Rectangle({
            size: [cw, h],
            point: startPoint,
            radius: cw / 2,
            stokeWidth: 0
        });
        rec.rotate((angle * 180) / Math.PI, pivotpoint);
        treepath.addChild(rec);

        // right leaf
        rec = new paper.Path.Rectangle({
            size: [cw, h],
            point: startPoint,
            radius: cw / 2,
            stokeWidth: 0
        });
        rec.rotate((-angle * 180) / Math.PI, pivotpoint);
        treepath.addChild(rec);

        return treepath;
    }
}
